import { NETWORK_PILL_CONFIG } from "./constants";

/**
 * Network source pill — colour-coded label of the upstream feed
 * partner. Each network has its own palette so admins can scan the
 * catalog vertically and spot per-network volume / coverage gaps.
 */
export function NetworkPill({ network }) {
  const n = network || "impact";
  const cfg = NETWORK_PILL_CONFIG[n] || { label: n, cls: "bg-slate-100 text-slate-700" };
  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}
      data-testid={`network-pill-${n}`}
    >
      {cfg.label}
    </span>
  );
}

/**
 * Inline CPA / CPC chip with admin toggle. Catalog sync auto-detects
 * CPC from PubAdHub's free-form commission_rate string but the
 * heuristic isn't perfect — click flips a brand's offer_type and
 * re-stamps every existing assignment server-side.
 */
export function OfferTypeChip({ brand, onToggle }) {
  const current = (brand.offer_type || "cpa").toLowerCase();
  const isCpc = current === "cpc";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle(isCpc ? "cpa" : "cpc");
      }}
      className={`inline-flex shrink-0 items-center whitespace-nowrap px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${
        isCpc
          ? "bg-teal-100 text-teal-800 border border-teal-200 hover:bg-teal-200"
          : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"
      }`}
      title={isCpc
        ? "CPC offer — click to flip to CPA"
        : "CPA offer — click to flip to CPC (pub earns split of CPC per click, no conversion required)"}
      data-testid={`offer-type-chip-${brand.campaign_id}`}
    >
      {current}
    </button>
  );
}

/**
 * Catalog lifecycle pill for Admin → Brands STATUS column.
 *
 * Impact historically stored `contract_status` ("Active" / "Pending").
 * Siteplug / MRGE / FlexOffers / etc. use `status` (`active` /
 * `inactive` / `pending_review`). Prefer catalog `status` so vanished
 * upstream brands (e.g. Siteplug Michael Kors) show Inactive.
 */
export function CatalogStatusPill({ brand }) {
  const catalog = (brand?.status || "").toString().trim();
  const contract = (brand?.contract_status || "").toString().trim();
  const raw = catalog || contract;
  if (!raw) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-slate-400 border border-dashed border-slate-200"
        title="No status on this brand row"
        data-testid={`brand-status-${brand?.campaign_id || "unknown"}`}
      >
        —
      </span>
    );
  }

  const key = raw.toLowerCase().replace(/\s+/g, "_");
  const label = raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const cls =
    key === "active"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : key === "inactive" || key === "disabled" || key === "removed" || key === "deleted"
        ? "bg-slate-100 text-slate-600 border-slate-200"
        : key === "pending" || key === "pending_review" || key === "paused"
          ? "bg-amber-50 text-amber-800 border-amber-200"
          : "bg-slate-50 text-slate-600 border-slate-200";

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cls}`}
      title={
        catalog && contract && catalog.toLowerCase() !== contract.toLowerCase()
          ? `Catalog: ${catalog} · Contract: ${contract}`
          : `Status: ${label}`
      }
      data-testid={`brand-status-${brand?.campaign_id || "unknown"}`}
    >
      {label}
    </span>
  );
}

/**
 * Sync-status pill — distinguishes:
 *   • Manual (purple)        — admin-created brand, never synced from a feed
 *   • Feed + Overrides (amber) — feed-imported but admin has manual edits locked
 *   • (none)                 — pure feed brand with no overrides
 */
export function SyncStatusPill({ brand }) {
  if (brand.is_manual && !brand.last_synced_at) {
    return (
      <span
        className="inline-flex shrink-0 items-center whitespace-nowrap px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-800 border border-violet-200"
        title="Manually created — never synced from a feed partner"
        data-testid={`sync-pill-manual-${brand.campaign_id}`}
      >
        Manual
      </span>
    );
  }
  if (brand._manual_fields && brand._manual_fields.length > 0) {
    return (
      <span
        className="inline-flex shrink-0 items-center whitespace-nowrap px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
        title={`Feed-imported but ${brand._manual_fields.length} field(s) are manually overridden: ${brand._manual_fields.join(", ")}`}
        data-testid={`sync-pill-overrides-${brand.campaign_id}`}
      >
        Feed + Overrides
      </span>
    );
  }
  return null;
}

/**
 * Deeplink support chip — Yes when API `deeplink` is a non-empty URL
 * template; No when null/empty (not supported).
 */
export function DeeplinkBadge({ brand }) {
  const supported = Boolean(brand?.deeplink);
  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${
        supported
          ? "bg-sky-50 text-sky-800 border-sky-200"
          : "bg-slate-50 text-slate-500 border-slate-200"
      }`}
      title={
        supported
          ? `Deeplink supported — replace {url} in: ${brand.deeplink}`
          : "Deeplink not supported for this brand"
      }
      data-testid={`deeplink-badge-${brand.campaign_id || brand.brand_id}`}
    >
      {supported ? "Deeplink" : "No deeplink"}
    </span>
  );
}

/**
 * Live-approval badge — currently Siteplug-only. Rendered when a
 * brand carries `siteplug_is_live === true`, meaning the affiliate
 * account is approved for the offer AND actively driving traffic
 * (i.e. it appears in `/3.0/partner/live-offers`, not just the full
 * available-offers catalog). Available-only offers get no badge so
 * the row list still lets admins scan the entire catalog at a glance.
 */
export function LiveBadge({ brand }) {
  if (brand?.siteplug_is_live !== true) return null;
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200"
      title="This Siteplug offer is Live — approved and actively driving traffic"
      data-testid={`live-badge-${brand.campaign_id}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
      Live
    </span>
  );
}
